import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { ErrorRetry, PersonaMessages, useDebounce, useName } from '@bechtel/client-common';
import { GET_CREW_MEMBERS } from '@bechtel/client-gql';
import {
  AvatarSize,
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Combobox,
  FormControl,
  Icon,
  IconSize,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  theme,
} from '@bechtel/gds';
import { CrewMember, CrewMemberList, PagingInput, PersonaType, SearchCrewMemberInput } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { get } from 'lodash';
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { AvatarRegularStyled, HeadingStyled } from './styled';
const ItemComponent = ({ item }: any) => {
  const { i18n } = useLingui();
  const { getFullName } = useName();
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box css={{ width: '100%' }} justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
        <Box flexDirection={{ sm: 'column' }}>
          <HeadingStyled weight="600" css={{ paddingBottom: `${theme.spacings[300]}` }}>
            {getFullName(item.firstName, item.lastName)}
          </HeadingStyled>
          {item?.persona && (
            <Text size="300" color="grey.600" transform="capitalize">
              {i18n._(PersonaMessages[item.persona])}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};
const SelectedItemComponent = ({ item }: any) => {
  const { getFullName } = useName();
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box css={{ width: '100%' }} justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
        <Box flexDirection={{ sm: 'column' }}>
          <HeadingStyled weight="500">{item && getFullName(item.firstName, item.lastName)}</HeadingStyled>
        </Box>
      </Box>
    </Box>
  );
};

interface UseSelectorProps {
  selectedCrewMember: CrewMember | null;
  setSelectedCrewMember: Dispatch<SetStateAction<CrewMember | null>>;
  isWeldDashboard?: boolean;
}
export const UserSelector: FC<UseSelectorProps> = ({
  selectedCrewMember: dashboardSelectedUser,
  setSelectedCrewMember: setDashboardSelectedUser,
  isWeldDashboard,
}) => {
  const { getFullName } = useName();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 500);
  const itemToString = (item) => getFullName(item.firstName, item.lastName);

  const [getUserList, { data, loading, error, refetch, networkStatus }] = useLazyQuery<
    { crewMembers: CrewMemberList },
    { input: SearchCrewMemberInput; paging: PagingInput }
  >(GET_CREW_MEMBERS, { fetchPolicy: 'network-only' });
  const userList = get(data, 'crewMembers.items', []) as CrewMemberList['items'];

  useEffect(() => {
    getUserList({
      variables: {
        input: {
          searchKeyword: debouncedSearchText,
          personas: [
            PersonaType.SUPERINTENDENT,
            PersonaType.GENERAL_FOREMAN,
            PersonaType.FOREMAN,
            isWeldDashboard ? PersonaType.FIELD_WELD_ENGINEER : PersonaType.FIELD_ENGINEER,
          ],
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [debouncedSearchText, getUserList]);

  return (
    <Box
      mt={{ sm: '450' }}
      ml={{ sm: '600' }}
      mb={{ sm: '400' }}
      style={{ maxWidth: '320px' }}
      alignItems={{ sm: 'center' }}
    >
      {!loading && error && (
        <ErrorRetry refetch={refetch}>
          <Trans>There was a problem loading users</Trans>
        </ErrorRetry>
      )}
      <FormControl
        label={t`Select User`}
        css={{ marginBottom: theme.spacings[300], minWidth: '240px', fontFamily: theme.fontFamilies.graphik }}
      >
        <Combobox
          ariaLabel={t`superintendent`}
          size={TextFieldSize.MEDIUM}
          items={userList}
          appearance={TextFieldAppearance.NORMAL}
          itemString={itemToString}
          itemComponent={ItemComponent}
          selectedComponent={SelectedItemComponent}
          selectedItem={dashboardSelectedUser}
          onSelect={async (selectedItem: any) => {
            setDashboardSelectedUser(selectedItem);
          }}
          term={searchText}
          setTerm={setSearchText}
          handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchText(event.target.value);
          }}
          restorePreviousOnSkip
          loading={loading || networkStatus === NetworkStatus.refetch}
          messageOnNoResults={t`No users found`}
        ></Combobox>
      </FormControl>

      {dashboardSelectedUser && (
        <Box ml={{ sm: '400' }} mb={{ sm: '300' }}>
          <Button
            appearance={ButtonAppearance.SECONDARY}
            size={ButtonSize.MEDIUM}
            disabled={!dashboardSelectedUser}
            onClick={() => {
              setDashboardSelectedUser(null);
            }}
          >
            <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
              <Icon icon="xmark" size={IconSize.MEDIUM} fillColor="blue.1000" color="blue.1000" />
            </Box>
            <Text size="400" color="grey.1000" family="graphik">
              <Trans>Clear</Trans>
            </Text>
          </Button>
        </Box>
      )}
    </Box>
  );
};
